import i18n from "i18n";

const CANDIDATE_DELIMITERS = [",", ";", "\t", "|"];

// A headerless 3-column row is ambiguous: it could be [sourceTerm, targetTerm,
// context] (the common case, targetLanguage coming from the locale picker) or
// [targetLanguage, sourceTerm, targetTerm] (no context column, but an explicit
// per-row language). Locale codes ("fr", "en-US", "es-MX") essentially never
// collide with real glossary terms, so use the shape of column 0 to tell them
// apart rather than always assuming the first shape.
const LOCALE_CODE_PATTERN = /^[a-z]{2,3}(-[A-Z]{2})?$/;
const looksLikeLocaleCode = (value) => LOCALE_CODE_PATTERN.test(value);

export const DELIMITER_LABELS = {
	",": "comma",
	";": "semicolon",
	"\t": "tab",
	"|": "pipe",
};

export const detectCsvDelimiter = (text) => {
	const firstLine = (text ?? "").split(/\r\n|\r|\n/, 1)[0] ?? "";

	let inQuotes = false;
	const counts = new Map(CANDIDATE_DELIMITERS.map((delimiter) => [delimiter, 0]));

	for (const char of firstLine) {
		if (char === '"') {
			inQuotes = !inQuotes;
			continue;
		}
		if (!inQuotes && counts.has(char)) {
			counts.set(char, counts.get(char) + 1);
		}
	}

	let bestDelimiter = ",";
	let bestCount = 0;
	CANDIDATE_DELIMITERS.forEach((delimiter) => {
		const count = counts.get(delimiter);
		if (count > bestCount) {
			bestCount = count;
			bestDelimiter = delimiter;
		}
	});

	return bestDelimiter;
};

export const parseCsvText = (text, delimiter = ",") => {
	const rows = [];
	let current = "";
	let row = [];
	let inQuotes = false;

	for (let i = 0; i < text.length; i += 1) {
		const char = text[i];
		const next = text[i + 1];

		if (char === '"') {
			if (inQuotes && next === '"') {
				current += '"';
				i += 1;
			} else {
				inQuotes = !inQuotes;
			}
			continue;
		}

		if (char === delimiter && !inQuotes) {
			row.push(current);
			current = "";
			continue;
		}

		if ((char === "\n" || char === "\r") && !inQuotes) {
			if (char === "\r" && next === "\n") i += 1;
			row.push(current);
			rows.push(row);
			row = [];
			current = "";
			continue;
		}

		current += char;
	}

	if (current.length > 0 || row.length > 0) {
		row.push(current);
		rows.push(row);
	}

	return rows;
};

export const buildGlossaryImportPreview = ({ text, locale, existingEntries }) => {
	const delimiter = detectCsvDelimiter(text);
	const rawRows = parseCsvText(text, delimiter)
		.map((row) => row.map((cell) => (cell ?? "").trim()))
		.filter((row) => row.some((cell) => cell.length > 0));

	if (rawRows.length === 0) {
		throw new Error(i18n.t("message.csvIsEmpty"));
	}

	const normalizedHeader = rawRows[0].map((cell) => cell.toLowerCase());
	const hasHeader =
		normalizedHeader.includes("targetlanguage") ||
		normalizedHeader.includes("sourceterm") ||
		normalizedHeader.includes("targetterm") ||
		normalizedHeader.includes("context");

	const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
	const previewRows = [];
	const headerIndex = hasHeader
		? {
			targetLanguage: normalizedHeader.indexOf("targetlanguage"),
			sourceTerm: normalizedHeader.indexOf("sourceterm"),
			targetTerm: normalizedHeader.indexOf("targetterm"),
			context: normalizedHeader.indexOf("context"),
		}
		: null;

	dataRows.forEach((row) => {
		const isThreeColumnRow = !hasHeader && row.length <= 3;
		let targetLanguage = "";
		let sourceTerm = "";
		let targetTerm = "";
		let context = "";

		if (hasHeader) {
			targetLanguage =
				(row[headerIndex?.targetLanguage] || locale?.value || "").trim();
			sourceTerm = (row[headerIndex?.sourceTerm] || "").trim();
			targetTerm = (row[headerIndex?.targetTerm] || "").trim();
			context = (row[headerIndex?.context] || "").trim();
		} else if (isThreeColumnRow && looksLikeLocaleCode(row[0] || "")) {
			targetLanguage = (row[0] || "").trim();
			sourceTerm = (row[1] || "").trim();
			targetTerm = (row[2] || "").trim();
		} else if (isThreeColumnRow) {
			targetLanguage = (locale?.value || "").trim();
			sourceTerm = (row[0] || "").trim();
			targetTerm = (row[1] || "").trim();
			context = (row[2] || "").trim();
		} else {
			targetLanguage = (row[0] || locale?.value || "").trim();
			sourceTerm = (row[1] || "").trim();
			targetTerm = (row[2] || "").trim();
			context = (row[3] || "").trim();
		}

		if (!targetLanguage || !sourceTerm || !targetTerm) return;

		previewRows.push({ targetLanguage, sourceTerm, targetTerm, context });
	});

	const dedupedRows = Array.from(
		previewRows.reduce((acc, row) => {
			const key = `${row.targetLanguage.toLowerCase()}::${row.sourceTerm.toLowerCase()}`;
			acc.set(key, row);
			return acc;
		}, new Map()).values()
	);

	if (dedupedRows.length === 0) {
		throw new Error(i18n.t("message.noValidEntriesFound"));
	}

	const existingMap = new Map(
		(existingEntries ?? []).map((entry) => [
			`${(entry.targetLanguage ?? "").toLowerCase()}::${(entry.sourceTerm ?? "").toLowerCase()}`,
			entry,
		])
	);

	const replacedRecords = [];
	const newRecords = [];

	dedupedRows.forEach((row) => {
		const key = `${row.targetLanguage.toLowerCase()}::${row.sourceTerm.toLowerCase()}`;
		const existing = existingMap.get(key);
		if (existing) {
			replacedRecords.push({
				...row,
				existingTargetTerm: existing.targetTerm,
				existingContext: existing.context ?? "",
			});
			return;
		}
		newRecords.push(row);
	});

	return {
		delimiter,
		importedCount: dedupedRows.length,
		newCount: newRecords.length,
		replacedCount: replacedRecords.length,
		newRecords,
		replacedRecords,
	};
};
